import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Target, Flame, Star, Cpu, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { normalizeAdvice } from '../shared/formatters';

const PositionsTab = ({ 
  positions, 
  livePrices, 
  refreshing,
  onRefresh,
  onTabChange 
}) => {
  // Track previous prices for tick flash effect
  const [priceFlash, setPriceFlash] = useState({});
  const prevPricesRef = useRef({});
  // Track expanded AI analysis per position
  const [expandedAI, setExpandedAI] = useState({});

  // Detect price changes and trigger flash
  useEffect(() => {
    if (!livePrices) return;
    
    const newFlashStates = {};
    
    Object.keys(livePrices).forEach(symbol => {
      const currentPrice = livePrices[symbol];
      const prevPrice = prevPricesRef.current[symbol];
      
      if (prevPrice !== undefined && prevPrice !== currentPrice) {
        // Price changed - determine direction
        newFlashStates[symbol] = currentPrice > prevPrice ? 'up' : 'down';
        
        // Clear flash after 300ms
        setTimeout(() => {
          setPriceFlash(prev => ({ ...prev, [symbol]: null }));
        }, 300);
      }
    });
    
    if (Object.keys(newFlashStates).length > 0) {
      setPriceFlash(prev => ({ ...prev, ...newFlashStates }));
    }
    
    // Update previous prices
    prevPricesRef.current = { ...livePrices };
  }, [livePrices]);

  if (positions.length === 0) {
    return (
      <Card className="border-slate-700/50 bg-slate-900/50">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
            <Target className="h-8 w-8 text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Açık Pozisyon Yok</h3>
          <p className="text-slate-400 text-sm mb-4 max-w-sm mx-auto">
            Henüz aktif bir pozisyonunuz bulunmuyor. Yeni sinyaller için "Giriş Bekliyor" sekmesini takip edin.
          </p>
          <Button 
            onClick={() => onTabChange('active')}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Flame className="h-4 w-4 mr-2" />
            Bekleyen Sinyallere Git
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Status calculator with flat design approach
  const getStatus = (pos, livePnl = null) => {
    const durum = pos.durum || '';
    const pnl = livePnl !== null ? livePnl : (pos.mevcut_pozisyon?.pnl_yuzde || 0);
    
    // TP3 hit - Full success
    if (durum.includes('TP3') && durum.includes('GEÇİLDİ')) return {
      label: 'TP3 Ulaştı',
      icon: '🏆',
      color: 'purple',
      advice: 'Tebrikler! Tüm hedeflere ulaşıldı.',
    };
    // TP2 hit
    if (durum.includes('TP2') && durum.includes('GEÇİLDİ')) return {
      label: 'TP2 Ulaştı',
      icon: '🎯',
      color: 'blue',
      advice: 'Kârın bir kısmı güvenceye alınabilir.',
    };
    // TP1 hit
    if (durum.includes('TP1') && durum.includes('GEÇİLDİ')) return {
      label: 'TP1 Ulaştı',
      icon: '✓',
      color: 'green',
      advice: 'Stop seviyesi yükseltilebilir.',
    };
    // Close to TP1 and profitable
    if (durum.includes('TP1') && durum.includes('YAKIN') && pnl > 0) return {
      label: 'Hedefe Yakın',
      icon: '●',
      color: 'green',
      advice: 'İlk hedefe yaklaşım devam ediyor.',
    };
    // Close to Stop and losing
    if (durum.includes('STOP') && durum.includes('YAKIN') && pnl < 0) return {
      label: 'Dikkat',
      icon: '⚠',
      color: 'amber',
      advice: 'Risk artıyor, dikkatli takip edilmeli.',
    };
    // General profitable
    if (pnl > 0) return {
      label: 'Kârlı',
      icon: '●',
      color: 'green',
      advice: 'Pozisyon yapısı güçlü görünüyor.',
    };
    // General losing
    if (pnl < 0) return {
      label: 'Takipte',
      icon: '●',
      color: 'amber',
      advice: 'Kısa vadeli dalgalanma. Yapı korunuyor.',
    };
    // Neutral
    return {
      label: 'Aktif',
      icon: '●',
      color: 'slate',
      advice: 'Pozisyon aktif.',
    };
  };

  // Color map for status badge
  const colorMap = {
    green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    red: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
    slate: 'bg-slate-500/15 text-slate-400 border-slate-500/20',
  };

  // Signal Strength calculator
  const getSignalStrength = (pos) => {
    const score = pos.sniper_score || pos.kalite_skoru || 0;
    let grade = pos.sniper_grade || pos.sinyal_derece || '';
    
    if (!grade && score > 0) {
      if (score >= 80) grade = 'A+';
      else if (score >= 70) grade = 'A';
      else if (score >= 60) grade = 'B';
      else grade = 'C';
    }
    
    const gradeDisplay = {
      'A+': { label: 'Mükemmel', stars: 5, color: 'text-amber-400' },
      'A': { label: 'Çok Güçlü', stars: 4, color: 'text-amber-400' },
      'B': { label: 'Güçlü', stars: 3, color: 'text-amber-500' },
      'C': { label: 'İyi', stars: 2, color: 'text-slate-400' }
    };
    
    return gradeDisplay[grade] || gradeDisplay['C'];
  };

  // Star rating component with "Güç" label and tooltip
  const StarRating = ({ stars, className = '' }) => (
    <span 
      className={`inline-flex items-center gap-1.5 cursor-default ${className}`}
      title={`Sinyal Gücü: ${stars}/5`}
    >
      <span className="text-xs text-slate-400 font-medium">Güç</span>
      <span className="inline-flex">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-2.5 w-2.5 ${i < stars ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-slate-600'}`} 
          />
        ))}
      </span>
    </span>
  );

  // Card border/bg colors
  const cardColorMap = {
    green: { border: 'border-emerald-600/30', bg: 'bg-emerald-900/15' },
    amber: { border: 'border-amber-600/30', bg: 'bg-amber-900/15' },
    red: { border: 'border-rose-600/30', bg: 'bg-rose-900/15' },
    blue: { border: 'border-blue-600/30', bg: 'bg-blue-900/15' },
    purple: { border: 'border-purple-600/30', bg: 'bg-purple-900/15' },
    slate: { border: 'border-slate-600/30', bg: 'bg-slate-800/30' },
  };

  // Calculate progress for position (Giriş → Son Hedef yolculuğu)
  const calculateProgress = (pos, currentPrice) => {
    const entry = pos.mevcut_pozisyon?.giris_fiyati;
    // En son hedefi kullan (TP3 varsa TP3, yoksa TP2)
    const finalTarget = pos.mevcut_pozisyon?.tp3 || pos.mevcut_pozisyon?.tp2;
    
    if (!currentPrice || !entry || !finalTarget) return null;
    
    const isLong = pos.pozisyon_yonu === 'BUY';
    const totalRange = Math.abs(finalTarget - entry);
    
    let progress;
    if (isLong) {
      // LONG: Fiyat yukarı giderse ilerleme artar
      progress = ((currentPrice - entry) / totalRange) * 100;
    } else {
      // SHORT: Fiyat aşağı giderse ilerleme artar
      progress = ((entry - currentPrice) / totalRange) * 100;
    }
    
    // Negatif olabilir (fiyat yanlış yöne giderse) ama 0-100 arasında göster
    return Math.max(0, Math.min(100, progress));
  };

  // Calculate summary stats
  const calculateSummary = () => {
    let totalPnl = 0;
    let totalRealizedPnl = 0; // Gerçekleşen (alınan) kâr
    let profitable = 0;
    let atRisk = 0;

    positions.forEach(pos => {
      const symbolKey = pos.sembol?.includes('-') ? pos.sembol : `${pos.sembol}-USDT`;
      const livePrice = livePrices?.[symbolKey] || pos.mevcut_pozisyon?.guncel_fiyat;
      const entryPrice = pos.mevcut_pozisyon?.giris_fiyati;
      
      let pnl = 0;
      
      if (entryPrice && livePrice) {
        if (pos.pozisyon_yonu === 'BUY') {
          pnl = ((livePrice - entryPrice) / entryPrice) * 100;
        } else {
          pnl = ((entryPrice - livePrice) / entryPrice) * 100;
        }
      } else {
        pnl = pos.mevcut_pozisyon?.pnl_canli || pos.mevcut_pozisyon?.pnl_yuzde || 0;
      }
      
      totalPnl += pnl;
      if (pnl > 0) profitable++;
      
      // Riskte = Stop'a yakın olanlar
      if (pos.durum?.includes('STOP') && pos.durum?.includes('YAKIN')) atRisk++;
      
      // Gerçekleşen kâr hesapla (TP'lerden gelen)
      if (pos.mevcut_pozisyon?.pnl_kilitli) {
        totalRealizedPnl += pos.mevcut_pozisyon.pnl_kilitli;
      }
    });

    return { totalPnl, totalRealizedPnl, profitable, atRisk };
  };

  const summary = calculateSummary();

  // Format price helper (no $ symbol)
  const formatPrice = (price) => {
    if (!price) return '-';
    if (price >= 100) {
      return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else if (price >= 1) {
      return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    } else {
      return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Bar - Responsive */}
      <div className="p-3 sm:p-3.5 bg-slate-900/90 rounded-xl border border-slate-700/50">
        {summary.totalRealizedPnl > 0 ? (
          /* 5 metrik - Mobilde 4 (Negatifte gizli), Desktop'ta 5 */
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 sm:gap-3">
            {/* Pozisyon */}
            <div className="flex flex-col items-center justify-center px-1 sm:px-2 py-2 sm:py-2.5 bg-slate-800/50 rounded-lg">
              <span className="text-[9px] sm:text-xs text-slate-400 mb-0.5">Pozisyon</span>
              <span className="text-base sm:text-xl font-bold text-white">{positions.length}</span>
            </div>
            
            {/* Kazançta */}
            <div className="flex flex-col items-center justify-center px-1 sm:px-2 py-2 sm:py-2.5 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
              <span className="text-[9px] sm:text-xs text-emerald-400/70 mb-0.5">Kazançta</span>
              <span className="text-base sm:text-xl font-bold text-emerald-400">{summary.profitable}</span>
            </div>
            
            {/* Riskte - Sadece Desktop */}
            <div className={`hidden sm:flex flex-col items-center justify-center px-2 py-2.5 rounded-lg ${summary.atRisk > 0 ? 'bg-amber-900/15 border border-amber-500/15' : 'bg-slate-800/30'}`}>
              <span className={`text-xs mb-0.5 ${summary.atRisk > 0 ? 'text-amber-400/60' : 'text-slate-500'}`}>Riskte</span>
              <span className={`text-xl font-bold ${summary.atRisk > 0 ? 'text-amber-400/80' : 'text-slate-600'}`}>{summary.atRisk}</span>
            </div>
            
            {/* Anlık Durum */}
            <div className="flex flex-col items-center justify-center px-1 sm:px-2 py-2 sm:py-2.5 bg-slate-800/30 rounded-lg">
              <span className="text-[9px] sm:text-xs font-medium text-slate-500 mb-0.5">Anlık Durum</span>
              <span className={`text-sm sm:text-lg font-bold font-mono ${summary.totalPnl >= 0 ? 'text-slate-300' : 'text-slate-400'}`}>
                {summary.totalPnl >= 0 ? '+' : ''}{summary.totalPnl.toFixed(1)}%
              </span>
            </div>
            
            {/* Toplam Kazanç */}
            <div 
              className="flex flex-col items-center justify-center px-1 sm:px-2 py-2 sm:py-2.5 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 0 12px rgba(16, 185, 129, 0.15)'
              }}
            >
              <span className="text-[9px] sm:text-xs font-semibold text-emerald-400/80 mb-0.5">Toplam Kazanç</span>
              <span className="text-sm sm:text-xl font-bold font-mono text-emerald-400" style={{ textShadow: '0 0 10px rgba(16, 185, 129, 0.4)' }}>
                +{summary.totalRealizedPnl.toFixed(1)}%
              </span>
            </div>
          </div>
        ) : (
          /* 4 metrik - Mobilde 3 (Negatifte gizli), Desktop'ta 4 */
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {/* Pozisyon */}
            <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-slate-800/50 rounded-lg">
              <span className="text-[10px] sm:text-xs text-slate-400 mb-0.5">Pozisyon</span>
              <span className="text-lg sm:text-xl font-bold text-white">{positions.length}</span>
            </div>
            
            {/* Kazançta */}
            <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
              <span className="text-[10px] sm:text-xs text-emerald-400/70 mb-0.5">Kazançta</span>
              <span className="text-lg sm:text-xl font-bold text-emerald-400">{summary.profitable}</span>
            </div>
            
            {/* Riskte - Sadece Desktop */}
            <div className={`hidden sm:flex flex-col items-center justify-center px-2 py-2.5 rounded-lg ${summary.atRisk > 0 ? 'bg-amber-900/15 border border-amber-500/15' : 'bg-slate-800/30'}`}>
              <span className={`text-[10px] sm:text-xs mb-0.5 ${summary.atRisk > 0 ? 'text-amber-400/60' : 'text-slate-500'}`}>Riskte</span>
              <span className={`text-lg sm:text-xl font-bold ${summary.atRisk > 0 ? 'text-amber-400/80' : 'text-slate-600'}`}>{summary.atRisk}</span>
            </div>
            
            {/* Anlık Durum */}
            <div 
              className="flex flex-col items-center justify-center px-2 py-2.5 rounded-lg"
              style={{
                background: summary.totalPnl >= 0 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(100, 116, 139, 0.15) 0%, rgba(100, 116, 139, 0.05) 100%)',
                border: summary.totalPnl >= 0 ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(100, 116, 139, 0.3)',
                boxShadow: summary.totalPnl >= 0 ? '0 0 12px rgba(16, 185, 129, 0.15)' : 'none'
              }}
            >
              <span className={`text-[10px] sm:text-xs font-semibold mb-0.5 ${summary.totalPnl >= 0 ? 'text-emerald-400/80' : 'text-slate-500'}`}>Anlık Durum</span>
              <span 
                className={`text-lg sm:text-xl font-bold font-mono ${summary.totalPnl >= 0 ? 'text-emerald-400' : 'text-slate-400'}`}
                style={{ textShadow: summary.totalPnl >= 0 ? '0 0 10px rgba(16, 185, 129, 0.4)' : 'none' }}
              >
                {summary.totalPnl >= 0 ? '+' : ''}{summary.totalPnl.toFixed(1)}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Soft separator - breathing room between summary and cards */}
      <div className="h-3"></div>

      {/* Position Cards */}
      {positions.map((pos, idx) => {
        // Get live price
        const symbolKey = pos.sembol?.includes('-') ? pos.sembol : `${pos.sembol}-USDT`;
        const liveCurrentPrice = livePrices?.[symbolKey] || pos.mevcut_pozisyon?.guncel_fiyat;
        
        // Calculate live PnL
        const calculateLivePnl = () => {
          const entryPrice = pos.mevcut_pozisyon?.giris_fiyati;
          if (!entryPrice || !liveCurrentPrice) {
            return pos.mevcut_pozisyon?.pnl_canli || pos.mevcut_pozisyon?.pnl_yuzde || 0;
          }
          if (pos.pozisyon_yonu === 'BUY') {
            return ((liveCurrentPrice - entryPrice) / entryPrice) * 100;
          } else {
            return ((entryPrice - liveCurrentPrice) / entryPrice) * 100;
          }
        };
        const pnl = calculateLivePnl();
        const isProfit = pnl >= 0;
        const isLong = pos.pozisyon_yonu === 'BUY';
        
        // Get status
        const status = getStatus(pos, pnl);
        const cardColors = cardColorMap[status.color];
        
        // Get signal strength
        const strength = getSignalStrength(pos);
        
        // Calculate progress
        const progress = calculateProgress(pos, liveCurrentPrice);
        
        // Check for locked profit
        const hasLockedProfit = (pos.mevcut_pozisyon?.tp1_hit || pos.mevcut_pozisyon?.tp2_hit || pos.mevcut_pozisyon?.tp3_hit) && 
                               pos.mevcut_pozisyon?.pnl_kilitli !== undefined && 
                               pos.mevcut_pozisyon?.pnl_kilitli !== null;
        
        return (
          <Card 
            key={idx} 
            className={`border overflow-hidden ${cardColors.border} ${cardColors.bg} hover:border-slate-500/50 transition-colors`}
          >
            <CardContent className="p-4">
              {/* Row 1: Header - Status + Symbol (DOMINANT) + Direction + Stars */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  {/* Status Badge - Compact */}
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${colorMap[status.color]}`}>
                    <span>{status.icon}</span>
                    <span>{status.label}</span>
                  </span>
                  {/* Symbol - DOMINANT */}
                  <span className="font-bold text-white text-lg ml-1">{pos.sembol}</span>
                  {/* Direction Badge - Smaller */}
                  <Badge className={`text-[10px] px-1 py-0 font-medium ${isLong ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                    {isLong ? 'LONG' : 'SHORT'}
                  </Badge>
                  {/* Stars */}
                  <StarRating stars={strength.stars} />
                </div>
                {/* Kazanç - Sadece kilitli kâr varsa göster */}
                {hasLockedProfit && (
                  <div className="text-right">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Kazanç</p>
                    <span className="text-lg font-semibold font-mono text-emerald-400">
                      +{pos.mevcut_pozisyon.pnl_kilitli.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
              
              {/* Row 2: Giriş → Güncel (DOMINANT LIVE) → Anlık Durum */}
              <div className="flex items-center justify-between mb-2 py-2 px-3 bg-slate-900/30 rounded-lg">
                {/* Giriş - Secondary */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 uppercase">Giriş</span>
                  <span className="text-sm font-mono text-slate-400">{formatPrice(pos.mevcut_pozisyon?.giris_fiyati)}</span>
                </div>
                
                {/* Minimal separator */}
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                
                {/* Güncel - Simple with Tick Flash color only */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 uppercase font-medium">Güncel</span>
                  <span 
                    className={`text-xl font-bold font-mono transition-colors duration-150 ${
                      priceFlash[symbolKey] === 'up' 
                        ? 'text-emerald-400' 
                        : priceFlash[symbolKey] === 'down' 
                          ? 'text-red-400' 
                          : 'text-white'
                    }`}
                  >
                    {formatPrice(liveCurrentPrice)}
                  </span>
                </div>
                
                {/* Minimal separator */}
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                
                {/* Anlık Durum - Slightly brighter */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 uppercase">Anlık Durum</span>
                  <span className={`text-sm font-bold font-mono ${isProfit ? 'text-slate-200' : 'text-slate-300'}`}>
                    {isProfit ? '+' : ''}{pnl.toFixed(2)}%
                  </span>
                </div>
              </div>
              
              {/* Soft separator before TP grid */}
              <div className="h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent mb-3"></div>
              
              {/* Row 3: Target Grid - TP1/TP2/TP3 + Stop */}
              <div className="grid grid-cols-4 gap-2 mt-1">
                {/* TP1 */}
                <div className={`py-2.5 px-3 rounded-lg border text-center transition-all duration-150 cursor-default ${
                  pos.mevcut_pozisyon?.tp1_hit 
                    ? 'bg-emerald-900/30 border-emerald-500/40' 
                    : 'bg-slate-800/60 border-slate-700/40 hover:border-slate-600/60 hover:bg-slate-800/80'
                }`}>
                  <p className="text-xs text-slate-400 mb-0.5">{pos.mevcut_pozisyon?.tp1_hit ? '✓' : '○'} TP1</p>
                  <p className={`text-sm font-semibold font-mono ${pos.mevcut_pozisyon?.tp1_hit ? 'text-emerald-400' : 'text-slate-200'}`}>
                    {formatPrice(pos.mevcut_pozisyon?.tp1)}
                  </p>
                </div>
                {/* TP2 */}
                <div className={`py-2.5 px-3 rounded-lg border text-center transition-all duration-150 cursor-default ${
                  pos.mevcut_pozisyon?.tp2_hit 
                    ? 'bg-emerald-900/30 border-emerald-500/40' 
                    : 'bg-slate-800/60 border-slate-700/40 hover:border-slate-600/60 hover:bg-slate-800/80'
                }`}>
                  <p className="text-xs text-slate-400 mb-0.5">{pos.mevcut_pozisyon?.tp2_hit ? '✓' : '○'} TP2</p>
                  <p className={`text-sm font-semibold font-mono ${pos.mevcut_pozisyon?.tp2_hit ? 'text-emerald-400' : 'text-slate-200'}`}>
                    {formatPrice(pos.mevcut_pozisyon?.tp2)}
                  </p>
                </div>
                {/* TP3 */}
                <div className={`py-2.5 px-3 rounded-lg border text-center transition-all duration-150 cursor-default ${
                  pos.mevcut_pozisyon?.tp3_hit 
                    ? 'bg-emerald-900/30 border-emerald-500/40' 
                    : 'bg-slate-800/60 border-slate-700/40 hover:border-slate-600/60 hover:bg-slate-800/80'
                }`}>
                  <p className="text-xs text-slate-400 mb-0.5">{pos.mevcut_pozisyon?.tp3_hit ? '✓' : '○'} TP3</p>
                  <p className={`text-sm font-semibold font-mono ${pos.mevcut_pozisyon?.tp3_hit ? 'text-emerald-400' : 'text-slate-200'}`}>
                    {formatPrice(pos.mevcut_pozisyon?.tp3)}
                  </p>
                </div>
                {/* STOP - Very subtle (border only, minimal fill) */}
                <div className="py-2.5 px-3 bg-slate-800/20 rounded-lg border border-amber-700/25 text-center hover:border-amber-600/35 transition-all duration-150 cursor-default">
                  <p className="text-xs text-amber-600/60 mb-0.5">🛡️ Stop</p>
                  <p className="text-sm font-medium font-mono text-amber-600/70">{formatPrice(pos.mevcut_pozisyon?.stop_loss)}</p>
                </div>
              </div>
              
              {/* Row 5: AI Analysis - Expandable */}
              {(() => {
                // v10.5: normalizeAdvice ile eski emir kiplerini dönüştür
                const rawSebep = pos.sebep || pos.commentary || pos.ai_commentary;
                const sebep = normalizeAdvice(rawSebep);
                const riskLevel = pos.risk_level || 'ORTA';
                const riskNotes = pos.risk_notes || [];
                const isExpanded = expandedAI[idx];
                const isLong = pos.pozisyon_yonu === 'BUY';
                
                // Risk level colors
                const riskColors = {
                  'DÜŞÜK': 'text-emerald-400 bg-emerald-500/10',
                  'LOW': 'text-emerald-400 bg-emerald-500/10',
                  'ORTA': 'text-amber-400 bg-amber-500/10',
                  'MEDIUM': 'text-amber-400 bg-amber-500/10',
                  'YÜKSEK': 'text-rose-400 bg-rose-500/10',
                  'HIGH': 'text-rose-400 bg-rose-500/10'
                };
                
                // Determine prefix based on TP status
                const getPrefix = () => {
                  if (pos.mevcut_pozisyon?.tp3_hit) return 'Güncelleme';
                  if (pos.mevcut_pozisyon?.tp2_hit || pos.mevcut_pozisyon?.tp1_hit) return 'Güncelleme';
                  return 'Durum';
                };
                const prefix = getPrefix();
                
                // v10.3: Premium language - ÖZET (kapalı) vs DETAY (açık) ayrımı
                // Kapalı state'te kısa özet, açık state'te tam metin
                // v10.4: Premium language - varyant kelimeler
                const generateSummary = () => {
                  if (pos.mevcut_pozisyon?.tp3_hit) {
                    return 'Tüm hedeflere ulaşıldı';
                  }
                  if (pos.mevcut_pozisyon?.tp2_hit) {
                    return isLong ? 'Yapı güçleniyor, TP2 sonrası' : 'Aşağı yönlü yapı devam ediyor';
                  }
                  if (pos.mevcut_pozisyon?.tp1_hit) {
                    return isLong ? 'Trend sürüyor, TP1 sonrası' : 'SHORT senaryo geçerli';
                  }
                  return isLong ? 'Pozisyon yapısı geçerli' : 'Aşağı yönlü yapı sürüyor';
                };
                
                // Detay metni - tam açıklama (option a - basit dil)
                const generateDetail = () => {
                  if (pos.mevcut_pozisyon?.tp3_hit) {
                    return 'Tüm hedeflere ulaşıldı. Pozisyon tamamlandı.';
                  }
                  if (pos.mevcut_pozisyon?.tp2_hit) {
                    return isLong 
                      ? 'Ana hedefe ulaşıldı. Kârın bir kısmı güvenceye alınabilir.'
                      : 'SHORT senaryo güçleniyor. Kârın bir kısmı güvenceye alınabilir.';
                  }
                  if (pos.mevcut_pozisyon?.tp1_hit) {
                    return isLong
                      ? 'İlk hedefe ulaşıldı. Stop seviyesi yükseltilebilir.'
                      : 'İlk hedef alındı. Stop seviyesi yükseltilebilir.';
                  }
                  return isLong 
                    ? 'Pozisyon takipte. Risk ve hedef seviyeleri izleniyor.'
                    : 'SHORT senaryo geçerli. Risk ve hedef seviyeleri kontrol altında.';
                };
                
                // Kapalı state için özet (sebep varsa ilk cümle, yoksa generate)
                const getSummaryText = () => {
                  if (sebep) {
                    // Sebep varsa ilk cümleyi al
                    const firstSentence = sebep.split('.')[0];
                    return firstSentence.length > 40 ? firstSentence.substring(0, 40) + '...' : firstSentence;
                  }
                  return generateSummary();
                };
                
                // Açık state için detay
                const displayText = sebep || status.advice || generateDetail();
                const summaryText = getSummaryText();
                
                return (
                  <div className="mt-3 pt-2 border-t border-slate-700/30">
                    {/* Collapsed View - Özet satır (tekrar yok) */}
                    <button
                      onClick={() => setExpandedAI(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      className="w-full flex items-center justify-between text-xs hover:bg-slate-800/30 rounded px-2 py-1.5 transition-colors group"
                    >
                      <div className="flex items-center gap-2 text-slate-400 min-w-0 flex-1">
                        <Cpu className="h-3 w-3 text-blue-400 flex-shrink-0" />
                        <span className="text-blue-400/80 font-medium flex-shrink-0">{prefix} ·</span>
                        <span className="text-slate-300 truncate">{summaryText}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-slate-500 text-[10px] group-hover:text-slate-300">Detay</span>
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3 text-slate-500 group-hover:text-slate-300" />
                        ) : (
                          <ChevronDown className="h-3 w-3 text-slate-500 group-hover:text-slate-300" />
                        )}
                      </div>
                    </button>
                    
                    {/* Expanded View - Full panel (detay) */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-2 p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                            {/* Başlık - terminal style */}
                            <div className="flex items-center gap-2 mb-1.5">
                              <Cpu className="h-3.5 w-3.5 text-blue-400" />
                              <span className="text-[10px] text-blue-400/80 font-medium uppercase tracking-wide">Sistem Analizi</span>
                            </div>
                            
                            {/* Detay metni - tırnak yok */}
                            <p className="text-sm text-slate-300 leading-relaxed mb-3 pl-5">
                              {displayText}
                            </p>
                            
                            {/* Risk Level - Only if sebep exists */}
                            {sebep && riskLevel && (
                              <div className="flex items-center gap-3 pl-5 mb-2">
                                <span className="text-[10px] text-slate-500 uppercase tracking-wide">Risk ·</span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${riskColors[riskLevel] || riskColors['ORTA']}`}>
                                  {riskLevel === 'LOW' || riskLevel === 'DÜŞÜK' ? '🟢 DÜŞÜK' : riskLevel === 'HIGH' || riskLevel === 'YÜKSEK' ? '🔴 YÜKSEK' : '🟡 ORTA'}
                                </span>
                              </div>
                            )}
                            
                            {/* Notlar - Max 2 */}
                            {riskNotes && riskNotes.length > 0 && (
                              <div className="pl-5">
                                <span className="text-[10px] text-slate-500 uppercase tracking-wide">Notlar</span>
                                <div className="space-y-1 mt-1">
                                  {riskNotes.slice(0, 2).map((note, noteIdx) => (
                                    <div key={noteIdx} className="flex items-center gap-2 text-[11px] text-slate-400">
                                      <span className="text-slate-600">•</span>
                                      <span>{note}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PositionsTab;
